type Teardown = () => void

// Tears pages down on two Turbolinks events:
// - `before-cache`: before cloning the page into the snapshot cache.
//   Skipped on `no-cache` pages (see TurbolinksCacheControl).
// - `before-render`: before replacing the <body>; fires on all pages.
//
// The events bubble from `document`, so these window listeners run after every
// document-level listener. Vue apps are already unmounted by teardown time.
const PAGE_TEARDOWN_EVENTS = [
  'turbolinks:before-cache',
  'turbolinks:before-render',
] as const

// One visit can dispatch the teardown events more than once (a cacheable page
// fires before-cache and then before-render), so each registration runs at
// most once per visit. Code that runs between two dispatches of one visit
// (custom elements connecting at the body swap) would otherwise be torn down
// before the page's own setup ever runs.
let pageGeneration = 0
let teardownSeenInGeneration = -1

if (typeof document !== 'undefined') {
  // Turbolinks dispatches this before the visit renders, so the counter is
  // already ahead of the visit's first teardown event.
  document.addEventListener('turbolinks:visit', () => {
    pageGeneration += 1
  })

  // `teardownSeenInGeneration` must be set before any listener that registers
  // a teardown mid-event (a Vue onUnmounted hook, say). Turbolinks dispatches
  // on `document` itself, where `capture` gives no ordering priority, so the
  // guarantee is that this module is evaluated before any consumer.
  PAGE_TEARDOWN_EVENTS.forEach((eventName) => {
    document.addEventListener(
      eventName,
      () => {
        teardownSeenInGeneration = pageGeneration
      },
      { capture: true }
    )
  })
}

const onPageTeardown = (fn: Teardown, once: boolean): (() => void) => {
  // A registration made after a visit's first teardown event (at a body swap,
  // or in a `turbolinks:load` handler) belongs to the incoming page: starting
  // `handledGeneration` at the current generation makes that visit's remaining
  // teardown events skip it, and the next `turbolinks:visit` re-arms it.
  let handledGeneration: number | null =
    teardownSeenInGeneration === pageGeneration ? pageGeneration : null

  const unsubscribe = (): void => {
    PAGE_TEARDOWN_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handleTeardown)
    })
  }

  const handleTeardown = (): void => {
    if (handledGeneration === pageGeneration) {
      return
    }
    handledGeneration = pageGeneration

    if (once) {
      unsubscribe()
    }

    fn()
  }

  PAGE_TEARDOWN_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleTeardown)
  })

  return unsubscribe
}

/**
 * Runs a teardown every time the page is replaced, for the lifetime of the
 * document. Register at module level.
 * @param fn - The teardown to run.
 * @returns A function that removes the listeners.
 */
export const onEachPageTeardown = (fn: Teardown): (() => void) =>
  onPageTeardown(fn, false)

/**
 * Runs a teardown the next time the page is replaced, then stops listening.
 * Register from per-page setup code.
 * @param fn - The teardown to run.
 * @returns A function that removes the listeners.
 */
export const onNextPageTeardown = (fn: Teardown): (() => void) =>
  onPageTeardown(fn, true)
